import { Ellipsoid } from '@skand/three-js-renderer/bin/private/math';
import { Color, Matrix4, Quaternion, Vector3 } from 'three';
import { PathType } from '../../../CoreModules';
import { toCartographic } from '../../../Utils';
/**
 * Default button colors.
 */
const IDLE_COLORS = [new Color(0xff7798), new Color(0x3ef07e), new Color(0x58bcfd)];
/**
 * Button colors when hovered over.
 */
const HOVER_COLORS = [new Color(0xfb3463), new Color(0x1cd760), new Color(0x18a0fb)];
/**
 * Transformation widget tool controller.
 */
export class TransformWidgetTool {
    constructor(target, widgetManager, navigationService) {
        Object.defineProperty(this, "widgetManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "navigationService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "translationButtons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotationButtons", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "buttonAxisMap", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "translationStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "rotationStep", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "target", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "running", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "onTargetMove", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.widgetManager = widgetManager;
        this.navigationService = navigationService;
        this.translationButtons = [];
        this.rotationButtons = [];
        this.buttonAxisMap = new Map();
        this.translationStep = 0.001;
        this.rotationStep = 1.7453293e-5; // 0.001 degrees
        this.target = target;
        this.running = false;
        this.onTargetMove = () => this.updateButtons();
    }
    /**
     * Update the control widget positions.
     */
    updateButtons() {
        const center = this.target.getPosition();
        const cartographic = toCartographic(center);
        // Orient the axes to the surface of the globe
        const axes = [new Vector3(), new Vector3(), new Vector3()];
        Ellipsoid.WGS_84.rotationBasisForCartographic(cartographic, null, axes[1], axes[0], axes[2]);
        // Update the paths of each widget
        for (let i = 0; i < axes.length; i++) {
            const translationButton = this.translationButtons[i];
            const rotationButton = this.rotationButtons[i];
            const current = axes[i].clone().negate();
            const orthogonal = axes[(i + 1) % axes.length].clone();
            translationButton.setPath({
                type: PathType.Line,
                origin: center,
                direction: current,
            });
            rotationButton.setPath({
                type: PathType.Circle,
                center,
                origin: center.clone().add(orthogonal.multiplyScalar(100)),
                normal: current,
                pivotCenter: true,
            });
            translationButton.resetProgress();
            rotationButton.resetProgress();
            // Rotate the buttons to face normal/direction
            const transform = new Matrix4();
            transform.lookAt(center, center.clone().add(current), new Vector3(0, 1, 0));
            const rotation = new Quaternion();
            rotation.setFromRotationMatrix(transform);
            translationButton.setRotation(rotation);
            rotationButton.setRotation(rotation);
            this.buttonAxisMap.set(translationButton, current);
            this.buttonAxisMap.set(rotationButton, current);
        }
    }
    /**
     * Handle widget controls translating the target model.
     */
    handleTranslationControls(onTransform) {
        for (let i = 0; i < 3; i++) {
            const button = this.translationButtons[i];
            button.unregisterAll();
            button.register('hover', () => {
                button.setColor(HOVER_COLORS[i]);
            });
            button.register('leave', () => {
                if (button.isGrabbed())
                    return;
                button.setColor(IDLE_COLORS[i]);
            });
            button.register('grab', () => {
                this.navigationService.disable();
                for (const otherButton of this.translationButtons) {
                    if (button === otherButton)
                        continue;
                    otherButton.hide();
                }
                for (const otherButton of this.rotationButtons) {
                    otherButton.hide();
                }
            });
            button.register('release', () => {
                this.navigationService.enable();
                for (const otherButton of this.translationButtons) {
                    otherButton.show();
                }
                for (const otherButton of this.rotationButtons) {
                    otherButton.show();
                }
                onTransform(this.target.getPosition(), this.target.getRotation());
                this.updateButtons();
            });
            button.register('move', (moveData) => {
                const { position } = moveData;
                const currPosition = this.target.getPosition();
                const displacement = position.clone().sub(currPosition);
                const distance = displacement.length();
                const roundedDistance = Math.floor(distance / this.translationStep) * this.translationStep;
                const translation = displacement.normalize().multiplyScalar(roundedDistance);
                this.target.setPosition(currPosition.add(translation));
            });
        }
    }
    /**
     * Handle widget controls for rotating the target model.
     */
    handleRotationControls(onTransform) {
        for (let i = 0; i < 3; i++) {
            const button = this.rotationButtons[i];
            const initialRotation = new Quaternion();
            button.unregisterAll();
            button.register('hover', () => {
                button.setColor(HOVER_COLORS[i]);
            });
            button.register('leave', () => {
                if (button.isGrabbed())
                    return;
                button.setColor(IDLE_COLORS[i]);
            });
            button.register('grab', () => {
                this.navigationService.disable();
                this.target.getRotation(initialRotation);
                for (const otherButton of this.rotationButtons) {
                    if (button === otherButton)
                        continue;
                    otherButton.hide();
                }
                for (const otherButton of this.translationButtons) {
                    otherButton.hide();
                }
            });
            button.register('release', () => {
                this.navigationService.enable();
                for (const otherButton of this.rotationButtons) {
                    otherButton.show();
                }
                for (const otherButton of this.translationButtons) {
                    otherButton.show();
                }
                onTransform(this.target.getPosition(), this.target.getRotation());
                this.updateButtons();
            });
            button.register('move', (moveData) => {
                const normal = this.buttonAxisMap.get(button);
                if (normal) {
                    const roundedAngle = Math.floor((moveData.progress * Math.PI * 2) / this.rotationStep) * this.rotationStep;
                    const rotation = new Quaternion().setFromAxisAngle(normal, roundedAngle);
                    this.target.setRotation(rotation.multiply(initialRotation));
                }
            });
        }
    }
    /**
     * Start the tool.
     *
     * @param onTransform
     */
    start(onTransform) {
        this.stop();
        this.running = true;
        this.translationButtons = IDLE_COLORS.map(color => this.widgetManager.createArrowWidget(100, color));
        this.rotationButtons = IDLE_COLORS.map(color => this.widgetManager.createRingWidget(75, color));
        this.updateButtons();
        this.handleTranslationControls(onTransform);
        this.handleRotationControls(onTransform);
        for (const button of this.translationButtons) {
            button.show();
        }
        for (const button of this.rotationButtons) {
            button.show();
        }
    }
    /**
     * Stop the tool.
     */
    stop() {
        this.running = false;
        for (const button of this.translationButtons) {
            button.destroy();
        }
        for (const button of this.rotationButtons) {
            button.destroy();
        }
    }
    /**
     * Check if the tool is active.
     *
     * @returns
     */
    isActive() {
        return this.running;
    }
    /**
     * Get the target model.
     *
     * @returns
     */
    getTarget() {
        return this.target;
    }
    /**
     * Set the translation widget step.
     *
     * @param step
     */
    setTranslationStep(step) {
        this.translationStep = step;
    }
    /**
     * Set the rotation widget step.
     * @param step
     */
    setRotationStep(step) {
        this.rotationStep = step;
    }
}
